import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  useMediaQuery,
  Grid,
  Popover,
  Box,
  Snackbar,
  Alert,
  Slide,
  Typography,
  Card,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { SketchPicker } from "react-color";
import { useSettings } from "context/SettingsContext";
import AnseraLogo from "assets/images/Anser.png";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL } from "constants";
import { useSystemTheme } from "context/SystemThemeContext";

// Helper function for getting search icon URL
const getSearchIconLogoUrl = (option) => {
  // // console.log('getSearchIconLogoUrl called with option:', option);
  switch(option) {
    case "gray":
      return "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Gray.PNG";
    case "white":
      return "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-White.PNG";
    case "teal":
    default:
      return "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG";
  }
};

// Helper to extract logo option from script name
const getLogoOptionFromScriptName = (scriptName) => {
  if (!scriptName) return "teal";
  
  if (scriptName.includes("Gray")) return "gray";
  if (scriptName.includes("White")) return "white";
  if (scriptName.includes("Teal")) return "teal";
  return "teal";
};

// Add this API call function at the top level
const updateWebflowHeaderIcon = async (
  iconUrl, 
  domainToken, 
  disableIcon = false, 
  version = "0.0.6", 
  logoOption = "teal",
  logoPosition = "left",
  iconSpacing = "8",
  autoPublish = true 
) => {
  try {
    if (!domainToken) {
      console.error('Domain token is required but not provided');
      throw new Error('Domain token is required');
    }

    let script = "";
    let header_icon = "True";
    let display_name = "";
    
    const versionWithoutDots = version.replace(/\./g, '');
    
    const spacingValue = parseInt(iconSpacing) || 8;
    if (disableIcon) {
      if (logoOption && logoOption.startsWith("AnseraNavbar")) {
        display_name = logoOption;
      } else {
        const positionCode = logoPosition.charAt(0).toUpperCase();
        display_name = `AnseraNavbar${logoOption}Iconv${versionWithoutDots}${positionCode}${spacingValue}`;
      }
      script = "";
      header_icon = "False";
    } else {
      const finalIconUrl = iconUrl || getSearchIconLogoUrl(logoOption);
  script = `(function () {
  'use strict';

  const ICON_ID = 'ansera-search-icon';
  const ICON_URL = '${finalIconUrl}';
  const LOGO_POSITION = '${logoPosition}';
  const ICON_SPACING = ${spacingValue};
  const style = document.createElement('style');
  style.id = 'ansera-search-icon-styles';
 style.textContent =
  '.ansera-search-link {' +
  '  display: inline-flex !important;' +
  '  align-items: center !important;' +
  '  justify-content: ${logoPosition} !important;' +
  '  cursor: pointer;' +
  '  padding: 10px;' +
  '  line-height: normal;' +
  '  flex-shrink: 0;' +
  '  margin-left: ' + ICON_SPACING + 'px !important;' +
  '  box-sizing: border-box;' +
  '}' +
  '@media screen and (max-width: 991px) {' +
  '  .ansera-search-link {' +
  '    margin-left: 0 !important;' +
  '    width: 100% !important;' +
  '    justify-content: ' + (
      LOGO_POSITION === 'left'   ? 'flex-start' :
      LOGO_POSITION === 'center' ? 'center'     : 'flex-end'
    ) + ' !important;' +
  '  }' +
  '  li > .ansera-search-link {' +
  '    padding: 12px 20px !important;' +
  '  }' +
  '}';

  // Only inject once
  if (!document.getElementById('ansera-search-icon-styles')) {
    document.head.appendChild(style);
  }

  function getNavItemSize(el) {
    if (!el) return 20;
    const rect = el.getBoundingClientRect();
    if (rect.height > 0) return Math.max(16, rect.height * 0.55);
    const cs = getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight);
    const fs = parseFloat(cs.fontSize);
    if (!isNaN(lh)) return lh * 0.9;
    if (!isNaN(fs)) return fs * 0.9;
    return 20;
  }

  function createSearchLink(container) {
    const a = document.createElement('a');
    a.id = ICON_ID;
    a.href = '#';
    a.className = 'ansera-search-link';
    a.style.cssText =
      'cursor:pointer;' +
      'padding:8px;' +
      'line-height:normal;' +
      'flex-shrink:0;';

    a.onclick = e => {
      e.preventDefault();
      if (typeof window.ansera_search_show === 'function') {
        window.ansera_search_show();
      }
    };

    const ref =
      container.querySelector('a[href]:not(#' + ICON_ID + ')') ||
      container.querySelector('a');

    const size = getNavItemSize(ref);

    const img = document.createElement('img');
    img.src = ICON_URL;
    img.alt = 'Search';
    img.style.cssText =
      'width:' + size + 'px;' +
      'height:' + size + 'px;' +
      'display:block;' +
      'max-width:none;' +
      'max-height:none;' +
      'object-fit:contain;';

    img.onerror = () => (a.textContent = '🔍');

    a.appendChild(img);
    return a;
  }

  function createSearchLI(container) {
    const li = document.createElement('li');
    li.style.cssText =
      'display:flex;' +
      'align-items:center;' +
      'width:100%;';          // ← needed so justify-content works on mobile
    li.appendChild(createSearchLink(container));
    return li;
  }

  function injectIntoWebflowNav() {
    let done = false;
    document.querySelectorAll('.w-nav').forEach(nav => {
      const menu = nav.querySelector('.w-nav-menu');
      if (!menu || menu.querySelector('#' + ICON_ID)) return;

      const ul = [...menu.children].find(
        el => el.tagName === 'UL' && !el.closest('.w-dropdown-list')
      );

      if (ul) ul.appendChild(createSearchLI(menu));
      else menu.appendChild(createSearchLink(menu));

      done = true;
    });
    return done;
  }

  function injectIntoCustomNav() {
    let done = false;

    document.querySelectorAll('nav').forEach(nav => {
      if (nav.querySelector('#' + ICON_ID)) return;

      const linksContainer =
        nav.querySelector('.navbar_links') ||
        nav.querySelector('.nav_links') ||
        nav.querySelector('[class*="nav"][class*="link"]') ||
        nav;

      const links = [...linksContainer.querySelectorAll('a[href]')]
        .filter(a => !a.closest('.w-dropdown-list'));

      if (!links.length) return;

      linksContainer.appendChild(createSearchLink(linksContainer));
      done = true;
    });

    return done;
  }

  function inject() {
    if (document.getElementById(ICON_ID)) return true;
    return injectIntoWebflowNav() || injectIntoCustomNav();
  }

  function retry(max = 20) {
    let i = 0;
    const t = setInterval(() => {
      i++;
      if (inject() || i >= max) clearInterval(t);
    }, 400);
  }

  function observe() {
    if (!document.body) return;
    new MutationObserver(() => {
      if (!document.getElementById(ICON_ID)) retry(5);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', retry);
  } else {
    retry();
  }

  window.addEventListener('load', retry);
  observe();
})();`;
      header_icon = "True";
      
      // Generate display_name with position and spacing encoded
      const positionCode = logoPosition.charAt(0).toUpperCase(); // L, C, R
      const logoCapitalized = logoOption.charAt(0).toUpperCase() + logoOption.slice(1);
      display_name = `AnseraNavbar${logoCapitalized}Iconv${versionWithoutDots}${positionCode}${spacingValue}`;
    }

    const response = await fetch(`${FAST_API_BASE_URL}/api/webflow-header-icon`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'DOMAIN-TOKEN': domainToken,
      },
      body: JSON.stringify({
        script: script,
        version: version,
        header_icon: header_icon,
        display_name: display_name,
        auto_publish: autoPublish,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error response:', errorText);
      throw new Error(`Failed to update Webflow header icon: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    
    // Return the display_name along with the result
    return {
      ...result,
      display_name: display_name,
      logoPosition: logoPosition,
      iconSpacing: spacingValue
    };
  } catch (error) {
    console.error('Error updating Webflow header icon:', error);
    throw error;
  }
};

 const StyledInput = ({ type = "text", value, onChange, name, placeholder, startAdornment, endAdornment }) => {
   const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
    return (
      <SoftBox sx={{ position: "relative", width: "100%" }}>
        {startAdornment && (
          <SoftBox
            sx={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              color: isDarkMode ? "#a0aec0" : "text.secondary", 
            }}
          >
            {startAdornment}
          </SoftBox>
        )}
        <input
          type={type}
          name={name}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          style={{
            width: "100%",
            padding: "12px 16px",
            paddingLeft: startAdornment ? "40px" : "16px",
            paddingRight: endAdornment ? "40px" : "16px",
            borderRadius: "8px",
            border: `1px solid ${isDarkMode ? "#4a5568" : "#d2d6da"}`, 
            backgroundColor: isDarkMode ? "#060C18" : "#ffffff", 
            fontSize: "0.875rem",
            fontWeight: 400,
            outline: "none",
            transition: "all 0.2s ease-in-out",
            boxSizing: "border-box",
            color: isDarkMode ? "#e2e8f0" : "#344767", 
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#219297";
            e.target.style.boxShadow = "0 0 0 2px #060C18";
            e.target.style.backgroundColor = isDarkMode ? "#060C18" : "#f8f9fa"; 
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkMode ? "#4a5568" : "#d2d6da"; 
            e.target.style.boxShadow = "none";
            e.target.style.backgroundColor = isDarkMode ? "#060C18" : "#ffffff"; 
          }}
        />
        {endAdornment && (
          <SoftBox
            sx={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              color: isDarkMode ? "#a0aec0" : "text.secondary", 
              fontSize: "0.75rem",
              fontWeight: 600,
            }}
          >
            {endAdornment}
          </SoftBox>
        )}
      </SoftBox>
    );
  };

const LoadingState = () => (
  <SoftBox textAlign="center" py={6}>
    <SoftTypography variant="h5" fontWeight="bold" gutterBottom color="text">
      Appearance tab will appear once the data sync is completed...
    </SoftTypography>
    <SoftTypography variant="body2" color="text.secondary" gutterBottom>
      Please wait while we load your settings
    </SoftTypography>
    <SoftBox mt={3} display="flex" justifyContent="center" gap={1}>
      {[0, 1, 2].map((index) => (
        <SoftBox
          key={index}
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#219197",
            animation: "pulse 1.4s infinite ease-in-out",
            animationDelay: `${index * 0.2}s`,
          }}
        />
      ))}
    </SoftBox>
    <style>
      {`
        @keyframes pulse {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          40% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
      `}
    </style>
  </SoftBox>
);

const WebflowPollingState = ({ pollingAttempts }) => (
  <SoftBox textAlign="center" py={6}>
    <SoftTypography variant="h5" fontWeight="bold" gutterBottom color="text">
      {pollingAttempts === 0 ? 'Setting up Webflow integration...' : 'Waiting for Webflow settings...'}
    </SoftTypography>
    <SoftTypography variant="body2" color="text.secondary" gutterBottom>
      {pollingAttempts === 0 
        ? 'Initializing connection to your Webflow site'
        : `Attempt ${pollingAttempts + 1} of 4 - Checking every 15 seconds`}
    </SoftTypography>
    <SoftBox mt={3} display="flex" justifyContent="center" gap={1}>
      {[0, 1, 2].map((index) => (
        <SoftBox
          key={index}
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: "#219197",
            animation: "pulse 1.4s infinite ease-in-out",
            animationDelay: `${index * 0.2}s`,
            opacity: pollingAttempts > 0 ? 1 : 0.6,
          }}
        />
      ))}
    </SoftBox>
    <SoftTypography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
      This may take a moment while we sync with your Webflow site. 
      {pollingAttempts > 0 && ' Please keep this tab open.'}
    </SoftTypography>
    <style>
      {`
        @keyframes pulse {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.6;
          }
          40% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
      `}
    </style>
  </SoftBox>
);

const RefreshingLoader = ({ message = "Refreshing settings..." }) => (
  <SoftBox 
    sx={{ 
      textAlign: "center", 
      py: 4,
      backgroundColor: "#f8f9fa",
      borderRadius: "12px",
      my: 2
    }}
  >
    <CircularProgress 
      size={40} 
      sx={{ 
        color: "#219297",
        mb: 2
      }} 
    />
    <SoftTypography variant="body2" color="text.secondary">
      {message}
    </SoftTypography>
  </SoftBox>
);

const NotificationBanner = ({ show, onClose, message, type = "info" }) => {
  if (!show) return null;

  const backgroundColor = type === "warning" ? "#fff3e0" : "#e8f5e9";
  const borderColor = type === "warning" ? "#ff9800" : "#4caf50";
  const textColor = type === "warning" ? "#e65100" : "#2e7d32";

  return (
    <Card
      sx={{
        backgroundColor: backgroundColor,
        border: "1px solid",
        borderColor: borderColor,
        borderRadius: "12px",
        p: 2,
        mb: 3,
        boxShadow: `0 4px 12px rgba(${type === "warning" ? "255, 152, 0, 0.2" : "76, 175, 80, 0.2"})`,
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" justifyContent="space-between" spacing={1}>
        <SoftTypography variant="body2" fontWeight="medium" sx={{ flex: 1, color: textColor }}>
          {message}
        </SoftTypography>
        <SoftButton
          variant="text"
          size="small"
          circular
          iconOnly
          onClick={onClose}
          sx={{ 
            minWidth: "auto", 
            p: 0.5,
            color: textColor 
          }}
        >
          ✕
        </SoftButton>
      </Stack>
    </Card>
  );
};

const SectionHeader = ({ title, description, icon }) => (
  <SoftBox mb={1}>
    <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
      {icon}
      <SoftTypography variant="h6" fontWeight="bold" color="#ffffff80">
        {title}
      </SoftTypography>
    </Stack>
    {description && (
      <SoftTypography variant="body2" color="text.secondary">
        {description}
      </SoftTypography>
    )}
  </SoftBox>
);

const SettingsRow = ({ label, element, isMobile, labelWidth = "220px" }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "flex-start", sm: "center" }}
    spacing={2}
    sx={{ py: 2, borderBottom: "1px solid", borderColor: "divider" }}
  >
    <SoftBox width={{ xs: "100%", sm: labelWidth }} minWidth={{ xs: "100%", sm: labelWidth }}>
      <SoftTypography variant="button" fontWeight="medium" color="text">
        {label}
      </SoftTypography>
    </SoftBox>
    <SoftBox flex={1} width="100%">
      {element}
    </SoftBox>
  </Stack>
);

const ColorPickerField = ({ label, field, color, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localColor, setLocalColor] = useState(color);
 const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorChange = (newColor) => {
    setLocalColor(newColor.hex);
    onChange(field, newColor);
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    setLocalColor(color);
  }, [color]);

 return (
      <Grid item xs={12} sm={6} lg={4}>
        <SoftTypography 
          variant="caption" 
          fontWeight="medium" 
          sx={{ color: isDarkMode ? "#e2e8f0" : undefined }} 
          gutterBottom
        >
          {label}
        </SoftTypography>
        <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
          <SoftBox
            sx={{
              width: 60,
              height: 36,
              backgroundColor: localColor,
              border: `2px solid ${isDarkMode ? "#4a5568" : "divider"}`, 
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "#219197",
                transform: "scale(1.05)",
              },
            }}
            onClick={handleClick}
          />
          <SoftBox flex={1}>
            <StyledInput
              value={localColor}
              onChange={(e) => {
                const newColor = e.target.value;
                setLocalColor(newColor);
                onChange(field, { hex: newColor });
              }}
              startAdornment="#"
            />
          </SoftBox>
        </Stack>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: isDarkMode ? "0 8px 32px rgba(0,0,0,0.3)" : "0 8px 32px rgba(0,0,0,0.12)", 
            }
          }}
        >
          <SketchPicker color={localColor} onChange={handleColorChange} />
        </Popover>
      </Grid>
    );
  };

const Appearance = () => {
  const [themeMode, setThemeMode] = useState("");
  const [appearance, setAppearance] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchIconOption, setSearchIconOption] = useState("https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG");
  const [searchIconLogoOption, setSearchIconLogoOption] = useState("teal");
  const [showNotification, setShowNotification] = useState(false);
  const [searchBubblePosition, setSearchBubblePosition] = useState("right");
  const [searchInputPosition, setSearchInputPosition] = useState("bottom");
  const [chatWidgetHeight, setChatWidgetHeight] = useState(0.75);
  const [searchBubbleBgColor, setSearchBubbleBgColor] = useState("#ffffff");
  const [searchBubbleIconOption, setSearchBubbleIconOption] = useState("https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG");
  const [showPublishNotification, setShowPublishNotification] = useState(false);
  const [searchBarCloseButtonColor, setSearchBarCloseButtonColor] = useState("#4a5568");
  const [enablePopupQuestion, setEnablePopupQuestion] = useState(false);
  const [popupQuestionLoadTimer, setPopupQuestionLoadTimer] = useState(20);
  const [popupQuestionCloseTimer, setPopupQuestionCloseTimer] = useState(10);
  const [logoPosition, setLogoPosition] = useState("center");
  const [iconSpacing, setIconSpacing] = useState("8");
  const [previousLogoPosition, setPreviousLogoPosition] = useState("left");
  const [previousIconSpacing, setPreviousIconSpacing] = useState("8");
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  const [customColors, setCustomColors] = useState({
    bg: "#ffffff",
    text: "#000000",
    buttonBg: "#1976d2",
    buttonHover: "#115293",
    inputBorder: "#cccccc",
    answerbgcolor: "#fdfdfd",
    tilesbg: "#f5f5f5",
    tilestextcolor: "#333333",
    chatheaderBg: "#ffffff",
    chatheadertextColor:" #000000",
    chatfooterBg: "#f1f1f1",
    chatfootertextColor: "#000000",
  });
  const [chatBotColors, setChatBotColors] = useState({
    bubbleBackground: "#219197",
    paneBackground: "#f9f9f9",
    questionBackground: "#ffffff",
    questionText: "#010103ff",
    hoverBackground: "#43d7dfff"
  });
  const [source, setSource] = useState("");
  const [domainToken, setDomainToken] = useState("");
  const [previousAppearance, setPreviousAppearance] = useState("");
  const [previousSearchIconLogoOption, setPreviousSearchIconLogoOption] = useState("teal");
  const [form, setForm] = useState({
    iconHeight: "",
    iconWidth: "",
    defaultSearchText: "",
    unresolvedMessage: "",
    contactLabel: "",
    contactURL: "",
    reviewMessage: "",
    emailText: "",
    headertext: "",
    chat_widget_header_text: "",
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [apiDebug, setApiDebug] = useState("");
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [hasRealSettings, setHasRealSettings] = useState(false);
  const pollingIntervalRef = useRef(null);
  const isSavingRef = useRef(false);

  const { settings, updateSettings, loading: settingsLoading, refreshSettings, isEmptySettings } = useSettings();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));

  const VERSION = "0.1.9";
  const WEBFLOW_DASHBOARD_URL = "https://webflow.com/dashboard";

  // Get search icon logo option from icon color setting
  const getSearchIconLogoOptionFromColor = (colorSetting) => {
    // // console.log('getSearchIconLogoOptionFromColor called with:', colorSetting);
    if (!colorSetting) return "teal";
    
    const color = colorSetting.toLowerCase();
    if (color.includes("gray")) return "gray";
    if (color.includes("white")) return "white";
    if (color.includes("teal")) return "teal";
    return "teal";
  };
  const getLogoPositionFromScriptName = (scriptName) => {
    if (!scriptName) return "left";
    const positionMatch = scriptName.match(/v\d+([LCR])\d+$/);
    if (positionMatch && positionMatch[1]) {
      const position = positionMatch[1].toLowerCase();
      if (position === 'l') return 'left';
      if (position === 'c') return 'center';
      if (position === 'r') return 'right';
    }
    return "left";
  };


  const getIconSpacingFromScriptName = (scriptName) => {
    if (!scriptName) return "8";

    const spacingMatch = scriptName.match(/[LCR](\d+)$/);
    if (spacingMatch && spacingMatch[1]) {
      return spacingMatch[1];
    }
    return "8";
  };

  const initializeData = useCallback(() => {
    if (settings && !initialLoadComplete) {
      // // console.log("Initializing data from settings");
      
      // Check if we have real settings or empty settings
      const hasData = settings._hasData === true;
      setHasRealSettings(hasData);
      
      if (!hasData) {
        // // console.log("Settings are empty or 404, NOT initializing form data");
        setInitialLoadComplete(true);
        return;
      } else {
        // Initialize with actual settings data
        setForm({
          iconHeight: settings.ansera_search_logo_height || "",
          iconWidth: settings.ansera_search_logo_width || "",
          defaultSearchText: settings.ansera_search_search_bar_text || "",
          unresolvedMessage: settings.no_answer_text || "",
          contactLabel: settings.ansera_search_contact_us_text || "",
          contactURL: settings.ansera_search_contact_us_url || "",
          reviewMessage: settings.ansera_search_feedback_message || "",
          emailText: settings.ansera_search_send_mail_text || "",
          headertext: settings.ansera_chat_header_text || "",
          chat_widget_header_text: settings.chat_widget_header_text || "",
        });

        setCustomColors({
          bg: settings.ansera_search_custom_bg_color || "#ffffff",
          text: settings.ansera_search_custom_text_color || "#000000",
          buttonBg: settings.ansera_search_custom_button_bg_color || "#1976d2",
          buttonHover: settings.ansera_search_custom_button_hover_color || "#115293",
          inputBorder: settings.ansera_search_custom_input_border_color || "#cccccc",
          answerbgcolor: settings.ansera_search_answer_bg_color || "#fdfdfd",
          tilesbg: settings.ansera_search_tiles_bg_color || "#f5f5f5",
          tilestextcolor: settings.ansera_search_tiles_text_color || "#333333",
          chatheaderBg: settings.ansera_search_chat_header_bg_color || "#ffffff",
          chatheadertextColor: settings.ansera_search_chat_header_text_color || "#000000",
          chatfooterBg: settings.ansera_search_chat_footer_bg_color || "#f1f1f1",
          chatfootertextColor: settings.ansera_search_chat_footer_text_color || "#000000",
        });

        setChatBotColors({
          bubbleBackground: settings.ansera_chat_bubble_background_color || "#219197",
          paneBackground: settings.ansera_chat_pane_background_color || "#f9f9f9",
          questionBackground: settings.ansera_chat_pane_question_background_color || "#ffffff",
          questionText: settings.ansera_chat_pane_question_text_color || "#010103ff",
          hoverBackground: settings.ansera_chat_bubble_hover_background_color || "#30b9c0ff"
        });

        const initialAppearance = settings.ansera_search_option3_widget || "search-bar";
        setAppearance(initialAppearance);
        setPreviousAppearance(initialAppearance);
        setSearchBubbleBgColor(settings.search_bubble_bg_color || "#219197");
        setSearchBubbleIconOption(settings.ansera_search_bubble_icon_url || "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG");
        setThemeMode(settings.ansera_search_theme || "light");
        setSearchBarCloseButtonColor(settings.search_bar_close_button_color || "#4a5568");
        setEnablePopupQuestion(settings.enable_popup_question === 1 || settings.enable_popup_question === true);
        setPopupQuestionLoadTimer(settings.popup_question_load_timer ?? 20);
        setPopupQuestionCloseTimer(settings.popup_question_close_timer ?? 10);
        setSearchBubblePosition(settings.ansera_search_bubble_position || "right");
        setSearchInputPosition(settings.ansera_search_input_position || "bottom");
        setChatWidgetHeight(settings.ansera_chat_widget_height || 0.75);
        const logoUrl = settings.ansera_search_logo_url ||
          "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG";
        setSearchIconOption(logoUrl);
        
        const iconColorSetting = settings.ansera_search_nav_icon_color;
        // console.log('Icon color setting from API:', iconColorSetting);
        
        let logoOption = getSearchIconLogoOptionFromColor(iconColorSetting);
        setSearchIconLogoOption(logoOption);
        setPreviousSearchIconLogoOption(logoOption);
        
        const scriptName = settings.ansera_search_nav_icon_script_name || "";
        if (scriptName) {
          const position = getLogoPositionFromScriptName(scriptName);
          const spacing = getIconSpacingFromScriptName(scriptName);
          setLogoPosition(position);
        setPreviousLogoPosition(position);
        setIconSpacing(spacing);
        setPreviousIconSpacing(spacing);
      }
      
      // console.log('Initialized appearance:', initialAppearance);
      // console.log('Initialized searchIconLogoOption:', logoOption);
    }
    
    setInitialLoadComplete(true);
  }
}, [settings, initialLoadComplete, currentDomain]);

  // Get source and domain token from localStorage
  useEffect(() => {
    const storedSource = localStorage.getItem("source");
    setSource(storedSource);
    
    const token = 
      localStorage.getItem("domainToken") || 
      localStorage.getItem("token") ||
      document.cookie.split('; ').find(row => row.startsWith('domainToken='))?.split('=')[1] ||
      document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] ||
      "";
    
    // console.log('Retrieved domain token:', token ? 'Found' : 'Not found');
    setDomainToken(token);
    
    if (storedSource === "webflow") {
      setShowNotification(true);
    }
  }, []);

  useEffect(() => {
    if (settings) {
      if (!initialLoadComplete && !isSavingRef.current) {
        initializeData();
      }
    }
  }, [settings, initialLoadComplete, initializeData, currentDomain]);

  useEffect(() => {
    // Only poll if source is webflow and we don't have real settings yet
    if (source === "webflow" && settings && isEmptySettings && pollingAttempts < 4) {
      // console.log(`Starting polling for Webflow settings (attempt ${pollingAttempts + 1})`);
      
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      pollingIntervalRef.current = setInterval(() => {
        setPollingAttempts(prev => {
          if (prev >= 3) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            // console.log('Max polling attempts reached');
            return prev;
          }
          // console.log(`Polling attempt ${prev + 1}`);
          refreshSettings();
          return prev + 1;
        });
      }, 15000); // Every 15 seconds

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [source, settings, isEmptySettings, pollingAttempts, refreshSettings]);

  useEffect(() => {
    if (hasRealSettings && pollingIntervalRef.current) {
      // console.log("Real settings loaded, stopping polling");
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, [hasRealSettings]);

  useEffect(() => {
    if (appearance && previousAppearance && appearance !== previousAppearance) {
      // console.log('Appearance changed from', previousAppearance, 'to', appearance);
    }
  }, [appearance, previousAppearance, source]);

  const handleColorChange = useCallback((field, color) => {
    setCustomColors((prev) => ({
      ...prev,
      [field]: color.hex,
    }));
  }, []);

  const handleChatBotColorChange = useCallback((field, color) => {
    setChatBotColors((prev) => ({
      ...prev,
      [field]: color.hex,
    }));
  }, []);

  const handleChange = useCallback((e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleThemeChange = useCallback((value) => {
    setThemeMode(value);
  }, []);

  const handleAppearanceChange = useCallback((value) => {
    // console.log('Appearance changing to:', value, 'from:', appearance);
    setAppearance(value);
  }, [appearance]);

  const handleSearchIconChange = useCallback((value) => {
    setSearchIconOption(value);
  }, []);
const handleSearchBubbleIconChange = useCallback((value) => {
  setSearchBubbleIconOption(value);
}, []);
  const handleSearchIconLogoChange = useCallback((value) => {
    // console.log('Search icon logo changed to:', value);
    setSearchIconLogoOption(value);
  }, []);

const handleSave = async () => {
    const logoPositionChanged = logoPosition !== previousLogoPosition;
    if (logoPositionChanged) {
      console.log('Logo position changed from', previousLogoPosition, 'to', logoPosition);
    }
    const iconSpacingChanged = iconSpacing !== previousIconSpacing;
    if (iconSpacingChanged) {
      console.log('Icon spacing changed from', previousIconSpacing, 'to', iconSpacing);
    }
    isSavingRef.current = true;
    try {
      if (!settings || isEmptySettings) {
        setSnackbarMessage("Cannot save: Settings not loaded yet");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      
      setLoading(true);
      setApiDebug("Starting save process...");
      
      const updates = {};
      let webflowIconUpdateNeeded = false;
      let newIconUrl = "";
      let disableWebflowIcon = false;
      let enableWebflowIcon = false;
      let showSnackbarForSearchIconEnabled = false;
      let isSearchIconLogoChange = false;
      
      const searchIconLogoChanged = 
        appearance === "search-icon" && 
        (searchIconLogoOption !== previousSearchIconLogoOption ||
         logoPositionChanged ||
         iconSpacingChanged);
      
      if (searchIconLogoChanged) {
        isSearchIconLogoChange = true;
        setApiDebug("Search icon logo change detected");
      }
      
      const appearanceChanged = appearance !== settings.ansera_search_option3_widget;
      const previousWasSearchIcon = settings.ansera_search_option3_widget === "search-icon";
      const nowIsSearchIcon = appearance === "search-icon";

      if (appearanceChanged) {
        updates.ansera_search_option3_widget = appearance;
        
        if (previousWasSearchIcon && !nowIsSearchIcon) {
          disableWebflowIcon = true;
        }
        else if (!previousWasSearchIcon && nowIsSearchIcon) {
          enableWebflowIcon = true;
          showSnackbarForSearchIconEnabled = true;
        }
      }
      if (logoPosition !== settings.ansera_search_icon_position) {
          updates.ansera_search_icon_position = logoPosition;
          console.log('Position changed to:', logoPosition);
      }

        if (parseInt(iconSpacing) !== settings.ansera_search_icon_spacing) {
          updates.ansera_search_icon_spacing = parseInt(iconSpacing);
          console.log('Spacing changed to:', iconSpacing);
      }
      if (searchBarCloseButtonColor !== settings.search_bar_close_button_color)
        updates.search_bar_close_button_color = searchBarCloseButtonColor;

      if ((enablePopupQuestion ? 1 : 0) !== settings.enable_popup_question)
        updates.enable_popup_question = enablePopupQuestion ? 1 : 0;

      if (Number(popupQuestionLoadTimer) !== settings.popup_question_load_timer)
        updates.popup_question_load_timer = Number(popupQuestionLoadTimer);

      if (Number(popupQuestionCloseTimer) !== settings.popup_question_close_timer)
        updates.popup_question_close_timer = Number(popupQuestionCloseTimer);

      if (themeMode !== settings.ansera_search_theme)
        updates.ansera_search_theme = themeMode;

      let logoUrl = "";
      if (appearance === "search-icon") {
        logoUrl = getSearchIconLogoUrl(searchIconLogoOption);
        
        const navIconColor = `${searchIconLogoOption.charAt(0).toUpperCase() + searchIconLogoOption.slice(1)}`;
        if (navIconColor !== settings.ansera_search_nav_icon_color) {
          updates.ansera_search_nav_icon_color = navIconColor;
        }
      } else {
        logoUrl = searchIconOption;
      }

      if (!logoUrl && appearance === "search-icon") {
        logoUrl = getSearchIconLogoUrl(searchIconLogoOption);
      }

      if (logoUrl !== settings.ansera_search_logo_url) {
        updates.ansera_search_logo_url = logoUrl;
        newIconUrl = logoUrl;
      } else if (appearance === "search-icon") {
        newIconUrl = logoUrl || settings.ansera_search_logo_url;
      }

      if (source === "webflow" && appearance === "search-icon") {
        if (logoUrl !== settings.ansera_search_logo_url || appearanceChanged || searchIconLogoChanged) {
          webflowIconUpdateNeeded = true;
        }
      }

      if (form.iconHeight !== settings.ansera_search_logo_height)
        updates.ansera_search_logo_height = form.iconHeight;
      if (form.chat_widget_header_text !== settings.chat_widget_header_text)
      updates.chat_widget_header_text = form.chat_widget_header_text;
      if (form.iconWidth !== settings.ansera_search_logo_width)
        updates.ansera_search_logo_width = form.iconWidth;

      if (form.defaultSearchText !== settings.ansera_search_search_bar_text)
        updates.ansera_search_search_bar_text = form.defaultSearchText;

      if (form.unresolvedMessage !== settings.no_answer_text)
        updates.no_answer_text = form.unresolvedMessage;

      if (form.contactLabel !== settings.ansera_search_contact_us_text)
        updates.ansera_search_contact_us_text = form.contactLabel;

      if (form.contactURL !== settings.ansera_search_contact_us_url)
        updates.ansera_search_contact_us_url = form.contactURL;

      if (form.reviewMessage !== settings.ansera_search_feedback_message)
        updates.ansera_search_feedback_message = form.reviewMessage;

      if (form.emailText !== settings.ansera_search_send_mail_text)
        updates.ansera_search_send_mail_text = form.emailText;

      if (form.headertext !== settings.ansera_chat_header_text)
        updates.ansera_chat_header_text = form.headertext;
      if (chatWidgetHeight !== settings.ansera_chat_widget_height)
          updates.ansera_chat_widget_height = chatWidgetHeight;
      if(searchInputPosition !== settings.ansera_search_input_position)
        updates.ansera_search_input_position = searchInputPosition;
      if (appearance === "chat-bot") {
        if (chatBotColors.bubbleBackground !== settings.ansera_chat_bubble_background_color)
          updates.ansera_chat_bubble_background_color = chatBotColors.bubbleBackground;
        if (chatBotColors.paneBackground !== settings.ansera_chat_pane_background_color)
          updates.ansera_chat_pane_background_color = chatBotColors.paneBackground;
        if (chatBotColors.questionBackground !== settings.ansera_chat_pane_question_background_color)
          updates.ansera_chat_pane_question_background_color = chatBotColors.questionBackground;
        if (chatBotColors.questionText !== settings.ansera_chat_pane_question_text_color)
          updates.ansera_chat_pane_question_text_color = chatBotColors.questionText;
        if(chatBotColors.hoverBackground !== settings.ansera_chat_bubble_hover_background_color)
          updates.ansera_chat_bubble_hover_background_color = chatBotColors.hoverBackground;
      }

      if (themeMode === "custom") {
        if (customColors.bg !== settings.ansera_search_custom_bg_color)
          updates.ansera_search_custom_bg_color = customColors.bg;
        if (customColors.text !== settings.ansera_search_custom_text_color)
          updates.ansera_search_custom_text_color = customColors.text;
        if (customColors.buttonBg !== settings.ansera_search_custom_button_bg_color)
          updates.ansera_search_custom_button_bg_color = customColors.buttonBg;
        if (customColors.buttonHover !== settings.ansera_search_custom_button_hover_color)
          updates.ansera_search_custom_button_hover_color = customColors.buttonHover;
        if (customColors.inputBorder !== settings.ansera_search_custom_input_border_color)
          updates.ansera_search_custom_input_border_color = customColors.inputBorder;
        if (customColors.answerbgcolor !== settings.ansera_search_answer_bg_color)
          updates.ansera_search_answer_bg_color = customColors.answerbgcolor;
        if (customColors.tilesbg !== settings.ansera_search_tiles_bg_color)
          updates.ansera_search_tiles_bg_color = customColors.tilesbg;
        if (customColors.tilestextcolor !== settings.ansera_search_tiles_text_color)
          updates.ansera_search_tiles_text_color = customColors.tilestextcolor;
        if(customColors.chatheaderBg !== settings.ansera_search_chat_header_bg_color)
          updates.ansera_search_chat_header_bg_color = customColors.chatheaderBg;
        if(customColors.chatfooterBg !== settings.ansera_search_chat_footer_bg_color)
          updates.ansera_search_chat_footer_bg_color = customColors.chatfooterBg;
        if (customColors.chatheadertextColor !== settings.ansera_search_chat_header_text_color)
          updates.ansera_search_chat_header_text_color = customColors.chatheadertextColor;
        if (customColors.chatfootertextColor !== settings.ansera_search_chat_footer_text_color)
          updates.ansera_search_chat_footer_text_color = customColors.chatfootertextColor;
      }
      if (appearance === "type-search") {
        if (searchBubbleBgColor !== settings.search_bubble_bg_color)
          updates.search_bubble_bg_color = searchBubbleBgColor;
        if (searchBubbleIconOption !== settings.ansera_search_bubble_icon_url)
          updates.ansera_search_bubble_icon_url = searchBubbleIconOption;
        if (searchBubblePosition !== settings.ansera_search_bubble_position)
          updates.ansera_search_bubble_position = searchBubblePosition;
      }
      if (webflowIconUpdateNeeded && !newIconUrl) {
        newIconUrl = getSearchIconLogoUrl(searchIconLogoOption);
      }

      if (Object.keys(updates).length === 0 && !disableWebflowIcon && !webflowIconUpdateNeeded && !enableWebflowIcon) {
        setSnackbarMessage("No changes to save!");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
        setLoading(false);
        setApiDebug("No changes detected");
        return;
      }

      let webflowSuccess = false;
      let webflowAction = "none";
      let settingsSuccess = false;
      let webflowErrorMessage = "";
      let newScriptName = "";

      if (Object.keys(updates).length > 0) {
        try {
          await updateSettings(updates);
          settingsSuccess = true;
          
          if (appearanceChanged) {
            setPreviousAppearance(appearance);
          }
          if (searchIconLogoChanged) {
            setPreviousSearchIconLogoOption(searchIconLogoOption);
          }
        } catch (error) {
          console.error('Failed to update settings:', error);
          setApiDebug(`Settings update failed: ${error.message}`);
          throw new Error(`Settings update failed: ${error.message}`);
        }
      }

      let latestSettings = settings;

      if (disableWebflowIcon || webflowIconUpdateNeeded || enableWebflowIcon) {
        if (!domainId) {
          console.error('Cannot update Webflow header icon: Domain token is missing');
          setApiDebug("Error: Domain token missing");
          throw new Error('Domain token is required for Webflow updates');
        } else {
          try {
            const currentScriptName = latestSettings?.ansera_search_nav_icon_script_name || "";
            
            if (isSearchIconLogoChange && appearance === "search-icon") {
              // Logo/position/spacing change: disable WITHOUT publishing, then enable WITH publishing
              setApiDebug("Changing search icon logo color...");
              
              // Step 1: disable — pass autoPublish=false to skip publish
              if (currentScriptName) {
                await updateWebflowHeaderIcon("", domainId, true, VERSION, currentScriptName, logoPosition, iconSpacing, false);
              } else {
                await updateWebflowHeaderIcon("", domainId, true, VERSION, previousSearchIconLogoOption, logoPosition, iconSpacing, false);
              }
              
              // Step 2: enable — autoPublish defaults to true, site gets published
              const enableResult = await updateWebflowHeaderIcon(
                getSearchIconLogoUrl(searchIconLogoOption), domainId, false, VERSION,
                searchIconLogoOption, logoPosition, iconSpacing
              );
              newScriptName = enableResult.display_name;
              
              webflowSuccess = true;
              webflowAction = "logo-change";
              setApiDebug("Search icon logo changed successfully");
            }
            else if (disableWebflowIcon) {
              // Switching away from search-icon: disable AND publish
              setApiDebug("Disabling Webflow header icon...");
              webflowAction = "disable";
              
              if (currentScriptName) {
                await updateWebflowHeaderIcon("", domainId, true, VERSION, currentScriptName, logoPosition, iconSpacing, true);
              } else {
                await updateWebflowHeaderIcon("", domainId, true, VERSION, "", logoPosition, iconSpacing, true);
              }
              webflowSuccess = true;
              setApiDebug("Webflow header icon disabled successfully");
            }
            else if (webflowIconUpdateNeeded || enableWebflowIcon) {
              // Fresh enable: publish
              setApiDebug("Updating Webflow header icon...");
              webflowAction = "enable";
              
              const enableResult = await updateWebflowHeaderIcon(
                newIconUrl, domainId, false, VERSION,
                searchIconLogoOption, logoPosition, iconSpacing
              );
              newScriptName = enableResult.display_name;
              webflowSuccess = true;
              setApiDebug("Webflow header icon updated successfully");
            }
          } catch (error) {
            console.error('Failed to update Webflow header icon:', error);
            webflowErrorMessage = error.message;
            setApiDebug(`Webflow icon update failed: ${error.message}`);
          }
        }
      }

      if (newScriptName) {
        try {
          await updateSettings({
            ansera_search_nav_icon_script_name: newScriptName
          });
        } catch (scriptNameError) {
          console.warn('Failed to update script name in settings:', scriptNameError);
        }
      }

      setTimeout(() => {
        refreshSettings().catch((e) => console.warn('Background refresh failed:', e));
      }, 2000); 

      let successMessage = "";
      let severity = "success";
      let shouldRedirectToWebflow = false;
      
      if (webflowSuccess && settingsSuccess) {
        if (webflowAction === "logo-change") {
          successMessage = "Settings saved! Please open your webflow site to see changes.";
          severity = "success";
          shouldRedirectToWebflow = true;
        } else if (webflowAction === "disable") {
          successMessage = "Settings updated and Webflow search icon disabled!";
        } else if (webflowAction === "enable") {
          successMessage = "Settings saved! Please open your webflow site to see changes.";
          severity = "success";
          shouldRedirectToWebflow = true;
        } else {
          successMessage = "Settings updated successfully!";
        }
      } else if (settingsSuccess && !webflowSuccess) {
        if (webflowErrorMessage) {
          successMessage = `Settings updated but Webflow icon failed: ${webflowErrorMessage}`;
          severity = "warning";
        } else {
          successMessage = "Settings updated successfully!";
        }
      } else if (!settingsSuccess && webflowSuccess) {
        if (webflowAction === "disable") {
          successMessage = "Webflow search icon disabled!";
        } else if (webflowAction === "logo-change") {
          successMessage = "Settings saved! Please open your webflow site to see changes.";
          severity = "success";
          shouldRedirectToWebflow = true;
        } else if (webflowAction === "enable") {
          successMessage = "Settings saved! Please open your webflow site to see changes.";
          severity = "success";
          shouldRedirectToWebflow = true;
        } else {
          successMessage = "Webflow search icon updated!";
        }
      }

      if (!successMessage && settingsSuccess) {
        successMessage = "Settings updated successfully!";
        severity = "success";
      }
      if (!successMessage && Object.keys(updates).length > 0) {
        successMessage = "Settings updated successfully!";
        severity = "success";
      }

      setSnackbarMessage(successMessage || "Settings updated successfully!");
      setSnackbarSeverity(severity || "success");
      setSnackbarOpen(true);

      if (shouldRedirectToWebflow) {
        setTimeout(() => {
          window.open(WEBFLOW_DASHBOARD_URL, '_blank');
        }, 3000);
      }

    } catch (error) {
      console.error("Failed to save settings:", error);
      setApiDebug(`Error: ${error.message}`);
      setSnackbarMessage(`Failed to save Settings: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      isSavingRef.current = false;
    }
  };

  // Show Webflow polling state when source is webflow and we have empty settings
  if (source === "webflow" && settings && isEmptySettings && pollingAttempts < 4) {
    return (
      <SoftBox
        sx={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          p: 4,
        }}
      >
        <WebflowPollingState pollingAttempts={pollingAttempts} />
      </SoftBox>
    );
  }

  // Show regular loading state for non-Webflow or after polling attempts
  if ((settingsLoading && !settings) || (!settings && refreshing)) {
    return (
      <SoftBox
        sx={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          p: 4,
        }}
      >
        <LoadingState />
        {refreshing && (
          <RefreshingLoader 
            message={``}
          />
        )}
      </SoftBox>
    );
  }

  // Show empty settings state - FORM SHOULD NOT BE DISPLAYED
  if (settings && isEmptySettings) {
    return (
      <SoftBox
        sx={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          p: 4,
        }}
      >
        <SoftBox textAlign="center" py={6}>
          <SoftTypography variant="h5" fontWeight="bold" gutterBottom color="text">
            Settings Not Found
          </SoftTypography>
          <SoftTypography variant="body2" color="text.secondary" gutterBottom>
            Please wait for settings to be initialized
          </SoftTypography>
          <SoftButton
            variant="contained"
            sx={{
              mt: 2,
              backgroundColor: "#219297",
              "&:hover": {
                backgroundColor: "#1a767a",
              }
            }}
            onClick={refreshSettings}
          >
            Refresh Settings
          </SoftButton>
        </SoftBox>
      </SoftBox>
    );
  }

  // Only show form if we have real settings
  if ( !initialLoadComplete || !hasRealSettings) {
    return (
      <SoftBox
        sx={{
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          p: 4,
        }}
      >
        <LoadingState />
      </SoftBox>
    );
  }

  const showRefreshingIndicator = refreshing && settings;

  return (
    <SoftBox
      sx={{
        backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
        borderRadius: "16px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === "warning" ? 3000 : 6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "8px",
            fontWeight: 500,
            color:"#ffffff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : snackbarSeverity === "warning"
                ? "#ed6c02"
                : snackbarSeverity === "info"
                ? "#0288d1"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {showRefreshingIndicator && (
        <RefreshingLoader 
          message={`Refreshing settings... (Attempt ${refreshCount + 1})`}
        />
      )}
      
      <SoftBox
        sx={{
          p: { xs: 2, sm: 3 },
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "white",
        }}
      >
        <NotificationBanner 
          show={showNotification} 
          onClose={() => setShowNotification(false)} 
          message="After installing Ansera app, please publish your website to add AI Chat Assistant Widget"
        />
        
        <Stack direction="row" alignItems="center" spacing={2}>
          <img src={AnseraLogo} alt="Ansera Logo" width={64} />
          <Box>
            <SoftTypography variant="h5" fontWeight="bold" color="#ffffff80">
              Appearance Settings
            </SoftTypography>
            <SoftTypography variant="body2" color="text.secondary">
              Customize how users interact with Ansera
            </SoftTypography>
          </Box>
        </Stack>
      </SoftBox>

      <SoftBox sx={{ p: { xs: 2, sm: 3 } }}>
<Card sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: "12px",
          backgroundColor: isDarkMode ? "#060C18" : "#ffffff", // ADDED
          border: isDarkMode ? "1px solid #2d2d3f" : "none", // ADDED
        }}>
          <SectionHeader
            title="Theme Settings"
            description="Choose the visual theme for your widget"
          />
          
          <SettingsRow
            label="Plugin Theme"
            isMobile={isMobile}
            element={
              <RadioGroup
                row={!isMobile}
                value={themeMode}
                onChange={(e) => handleThemeChange(e.target.value)}
                name="pluginTheme"
                sx={{ gap: isMobile ? 1 : 2 }}
              >
                <FormControlLabel
                  value="light"
                  control={
                    <Radio 
                      size="small" 
                      sx={{ 
                        color: isDarkMode ? "#a0aec0" : undefined, // ADDED
                        '&.Mui-checked': { color: "#219297" } // ADDED
                      }} 
                    />
                  }
                  label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Light</span>} 
                />
                <FormControlLabel
                  value="dark"
                  control={
                    <Radio 
                      size="small" 
                      sx={{ 
                        color: isDarkMode ? "#a0aec0" : undefined,
                        '&.Mui-checked': { color: "#219297" }
                      }} 
                    />
                  }
                  label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Dark</span>} 
                />
                <FormControlLabel
                  value="custom"
                  control={
                    <Radio 
                      size="small" 
                      sx={{ 
                        color: isDarkMode ? "#a0aec0" : undefined,
                        '&.Mui-checked': { color: "#219297" }
                      }} 
                    />
                  }
                  label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Custom</span>} 
                />
              </RadioGroup>
            }
          />

          {themeMode === "custom" && (
            <SettingsRow
              label="Custom Colors"
              isMobile={isMobile}
              element={
                <Grid container spacing={2}>
                  <ColorPickerField
                    label="Background Color"
                    field="bg"
                    color={customColors.bg}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Text Color"
                    field="text"
                    color={customColors.text}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Button Background Color"
                    field="buttonBg"
                    color={customColors.buttonBg}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Button Color on Hover"
                    field="buttonHover"
                    color={customColors.buttonHover}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Text Input Box Border"
                    field="inputBorder"
                    color={customColors.inputBorder}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Answer Area Background Color"
                    field="answerbgcolor"
                    color={customColors.answerbgcolor}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Answer Chat Header Background Color"
                    field="chatheaderBg"
                    color={customColors.chatheaderBg}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Answer Chat Header Text Color"
                    field="chatheadertextColor"
                    color={customColors.chatheadertextColor}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Answer Chat Footer Background Color"
                    field="chatfooterBg"
                    color={customColors.chatfooterBg}
                    onChange={handleColorChange}
                  />
                  <ColorPickerField
                    label="Answer Chat Footer Text Color"
                    field="chatfootertextColor"
                    color={customColors.chatfootertextColor}
                    onChange={handleColorChange}
                  />
                </Grid>
              }
            />
          )}
        </Card>

<Card sx={{ 
  p: 3, 
  mb: 3, 
  borderRadius: "12px",
  backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
  border: isDarkMode ? "1px solid #2d2d3f" : "none",
  transition: "background-color 0.3s ease, border-color 0.3s ease",
}}>
  <SectionHeader
    title="Widget Settings"
    description="Configure how the widget appears on your site"
  />

  <SettingsRow
    label="Widget Appearance"
    isMobile={isMobile}
    element={
      <RadioGroup 
        row={!isMobile} 
        value={appearance} 
        onChange={(e) => handleAppearanceChange(e.target.value)}
        sx={{ gap: isMobile ? 1 : 2 }}
      >
        <FormControlLabel 
          value="chat-bot" 
          control={
            <Radio 
              size="small" 
              sx={{ 
                color: isDarkMode ? "#a0aec0" : undefined,
                '&.Mui-checked': { color: "#219297" }
              }} 
            />
          } 
          label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Chatbot</span>}
        />
        <FormControlLabel 
          value="type-search" 
          control={
            <Radio 
              size="small" 
              sx={{ 
                color: isDarkMode ? "#a0aec0" : undefined,
                '&.Mui-checked': { color: "#219297" }
              }} 
            />
          } 
          label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Search Bar</span>}
        />
        {source === "webflow" && (
          <FormControlLabel 
            value="search-icon" 
            control={
              <Radio 
                size="small" 
                sx={{ 
                  color: isDarkMode ? "#a0aec0" : undefined,
                  '&.Mui-checked': { color: "#219297" }
                }} 
              />
            } 
            label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Search Icon</span>}
          />
        )}
      </RadioGroup>
    }
  />
  
  {appearance === "chat-bot" && (
    <Card sx={{ 
      p: 3, 
      mb: 3, 
      borderRadius: "12px",
      backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
      border: isDarkMode ? "1px solid #2d2d3f" : "1px solid #e0e0e0",
      transition: "background-color 0.3s ease, border-color 0.3s ease",
    }}>
      <SectionHeader
        title="Chat Bot Colors"
        description="Customize the appearance of your chat bot"
      />
      <Grid container spacing={2}>
        <ColorPickerField 
          label="Chat Widget Bubble Background" 
          field="bubbleBackground" 
          color={chatBotColors.bubbleBackground} 
          onChange={handleChatBotColorChange}
          isDarkMode={isDarkMode}
        />
        <ColorPickerField 
          label="Chat Widget Bubble MouseOver Background Color" 
          field="hoverBackground" 
          color={chatBotColors.hoverBackground} 
          onChange={handleChatBotColorChange}
          isDarkMode={isDarkMode}
        />
        <ColorPickerField 
          label="Chat Widget Initial Pane Background Color" 
          field="paneBackground" 
          color={chatBotColors.paneBackground} 
          onChange={handleChatBotColorChange}
          isDarkMode={isDarkMode}
        />
        <ColorPickerField 
          label="Chat Widget Pane Question Background Color" 
          field="questionBackground" 
          color={chatBotColors.questionBackground} 
          onChange={handleChatBotColorChange}
          isDarkMode={isDarkMode}
        />
        <ColorPickerField 
          label="Chat Widget Pane Question Text Color" 
          field="questionText" 
          color={chatBotColors.questionText} 
          onChange={handleChatBotColorChange}
          isDarkMode={isDarkMode}
        />
      </Grid>
    </Card>
  )}
        <SettingsRow
        label="Search input Position (In Chat window)"
        isMobile={isMobile}
        element={
          <RadioGroup
           row={!isMobile}
           value={searchInputPosition}
           onChange={(e)=>setSearchInputPosition(e.target.value)}
           sx={{gap:2}}
          >
            <FormControlLabel value="top" control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Top</span>} />
            <FormControlLabel value="bottom" control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Bottom</span>} />
          </RadioGroup>
        }
      />
  {appearance === "type-search" && (
    <>
      <SettingsRow
        label="Search Bar Icon"
        isMobile={isMobile}
        element={
          <RadioGroup
            row={!isMobile}
            value={searchIconOption}
            onChange={(e) => handleSearchIconChange(e.target.value)}
            sx={{ gap: 2 }}
          >
            {[
              {
                value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Gray.PNG",
                label: "Gray",
                bg: "transparent",
              },
              {
                value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-White.PNG",
                label: "White",
                bg: "#000000",
              },
              {
                value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG",
                label: "Teal",
                bg: "transparent",
              },
            ].map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio 
                    size="small" 
                    sx={{ 
                      color: isDarkMode ? "#a0aec0" : undefined,
                      '&.Mui-checked': { color: "#219297" }
                    }} 
                  />
                }
                label={
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "8px",
                      backgroundColor: option.bg,
                      border: `1px solid ${isDarkMode ? "#4a5568" : "#e0e0e0"}`,
                    }}
                  >
                    <img 
                      src={option.value} 
                      alt={option.label} 
                      style={{ 
                        width: 20, 
                        height: 20,
                        filter: isDarkMode && option.label === "White" ? "brightness(0) invert(1)" : "none"
                      }} 
                    />
                  </SoftBox>
                }
              />
            ))}
          </RadioGroup>
        }
      />
    
      <SettingsRow
        label="Search Bubble Background color (when search bar is closed)"
        isMobile={isMobile}
        element={
          <Grid container spacing={2}>
            <ColorPickerField
              label="Bubble Background Color"
              field="searchBubbleBgColor"
              color={searchBubbleBgColor}
              onChange={(field, color) => setSearchBubbleBgColor(color.hex)}
              isDarkMode={isDarkMode}
            />
          </Grid>
        }
      />
      
      <SettingsRow
        label="Search Bar Close Button Color"
        isMobile={isMobile}
        element={
          <Grid container spacing={2}>
            <ColorPickerField
              label="Close Button Color"
              field="searchBarCloseButtonColor"
              color={searchBarCloseButtonColor}
              onChange={(field, color) => setSearchBarCloseButtonColor(color.hex)}
              isDarkMode={isDarkMode}
            />
          </Grid>
        }
      />
      
      <SettingsRow
        label="Search Bubble Position (when search bar is closed)"
        isMobile={isMobile}
        element={
          <RadioGroup
           row={!isMobile}
           value={searchBubblePosition}
           onChange={(e)=>setSearchBubblePosition(e.target.value)}
           sx={{gap:2}}
          >
            <FormControlLabel value="left" control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Left</span>} />
            <FormControlLabel value="right" control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Right</span>} />
          </RadioGroup>
        }
      />

      <SettingsRow
        label="Search Bubble Icon (when search bar is closed)"
        isMobile={isMobile}
        element={
          <RadioGroup
            row={!isMobile}
            value={searchBubbleIconOption}
            onChange={(e) => handleSearchBubbleIconChange(e.target.value)}
            sx={{ gap: 2 }}
          >
            {[
              {
                value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Gray.PNG",
                label: "Gray",
                bg: "transparent",
              },
              {
                value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-White.PNG",
                label: "White",
                bg: "#000000",
              },
              {
                value: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG",
                label: "Teal",
                bg: "transparent",
              },
              ...(settings?.is_whitelabelled ? [
                {
                  value: "https://img.icons8.com/ios_filled/512/search--v2.png",
                  label: "Black Search Icon",
                  bg: "transparent",
                },
                {
                  value: "https://img.icons8.com/m_outlined/512/FFFFFF/search.png",
                  label: "White Search Icon",
                  bg: "#000000",
                },
                ...(settings?.search_bar_bubble_logo ? [{
                  value: `${settings.search_bar_bubble_logo}`,
                  label: "Custom logo",
                  bg: "transparent",
                }] : [])
              ] : []),
            ].map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio 
                    size="small" 
                    sx={{ 
                      color: isDarkMode ? "#a0aec0" : undefined,
                      '&.Mui-checked': { color: "#219297" }
                    }} 
                  />
                }
                label={
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "8px",
                      backgroundColor: option.bg,
                      border: `1px solid ${isDarkMode ? "#4a5568" : "#e0e0e0"}`,
                    }}
                  >
                    <img 
                      src={option.value} 
                      alt={option.label} 
                      style={{ 
                        width: 20, 
                        height: 20,
                        filter: isDarkMode && (option.label === "White" || option.label === "White Search Icon") 
                          ? "brightness(0) invert(1)" 
                          : "none"
                      }} 
                    />
                  </SoftBox>
                }
              />
            ))}
          </RadioGroup>
        }
      />
    </>
  )}

  {source === "webflow" && appearance === "search-icon" && (
    <>
      <SettingsRow
        label="Search Icon Logo"
        isMobile={isMobile}
        element={
          <RadioGroup
            row={!isMobile}
            value={searchIconLogoOption}
            onChange={(e) => handleSearchIconLogoChange(e.target.value)}
            sx={{ gap: 2 }}
          >
            {[
              {
                value: "gray",
                label: "Gray",
                iconUrl: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Gray.PNG",
                bg: "transparent",
              },
              {
                value: "white",
                label: "White",
                iconUrl: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-White.PNG",
                bg: "#000000",
              },
              {
                value: "teal",
                label: "Teal",
                iconUrl: "https://www.cloudtern.com/wp-content/plugins/ansera-search/images/Ansera-Logo-Teal.PNG",
                bg: "transparent",
              },
            ].map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio 
                    size="small" 
                    sx={{ 
                      color: isDarkMode ? "#a0aec0" : undefined,
                      '&.Mui-checked': { color: "#219297" }
                    }} 
                  />
                }
                label={
                  <SoftBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1,
                      borderRadius: "8px",
                      backgroundColor: option.bg,
                      border: `1px solid ${isDarkMode ? "#4a5568" : "#e0e0e0"}`,
                    }}
                  >
                    <img
                      src={option.iconUrl}
                      alt={option.label}
                      style={{ 
                        width: 20, 
                        height: 20,
                        filter: isDarkMode && option.value === "white" ? "brightness(0) invert(1)" : "none"
                      }}
                    />
                  </SoftBox>
                }
              />
            ))}
          </RadioGroup>
        }
      />
      
      <SettingsRow
        label="Search Icon Position (mobile)"
        isMobile={isMobile}
        element={
          <SoftBox>
            <RadioGroup
              row={!isMobile}
              value={logoPosition}
              onChange={(e) => setLogoPosition(e.target.value)}
              sx={{ gap: 2, mb: 1 }}
            >
              <FormControlLabel 
                value="left" 
                control={
                  <Radio 
                    size="small" 
                    sx={{ 
                      color: isDarkMode ? "#a0aec0" : undefined,
                      '&.Mui-checked': { color: "#219297" }
                    }} 
                  />
                } 
                label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Left</span>}
              />
              <FormControlLabel 
                value="center" 
                control={
                  <Radio 
                    size="small" 
                    sx={{ 
                      color: isDarkMode ? "#a0aec0" : undefined,
                      '&.Mui-checked': { color: "#219297" }
                    }} 
                  />
                } 
                label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Center</span>}
              />
              <FormControlLabel 
                value="right" 
                control={
                  <Radio 
                    size="small" 
                    sx={{ 
                      color: isDarkMode ? "#a0aec0" : undefined,
                      '&.Mui-checked': { color: "#219297" }
                    }} 
                  />
                } 
                label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Right</span>}
              />
            </RadioGroup>
          </SoftBox>
        }
      />
      
      <SettingsRow
        label="Icon Spacing (before icon)"
        isMobile={isMobile}
        element={
          <SoftBox maxWidth="200px">
            <StyledInput
              type="number"
              name="iconSpacing"
              value={iconSpacing}
              onChange={(e) => setIconSpacing(e.target.value)}
              placeholder="e.g. 8"
              endAdornment="px"
            />
          </SoftBox>
        }
      />
    </>
  )}

  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <SettingsRow
        label="Icon Height"
        isMobile={isMobile}
        element={
          <StyledInput
            name="iconHeight"
            type="number"
            value={form.iconHeight}
            onChange={handleChange}
            placeholder="Enter height"
            endAdornment="px"
          />
        }
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <SettingsRow
        label="Icon Width"
        isMobile={isMobile}
        element={
          <StyledInput
            name="iconWidth"
            type="number"
            value={form.iconWidth}
            onChange={handleChange}
            placeholder="Enter width"
            endAdornment="px"
          />
        }
      />
    </Grid>
  </Grid>
        <SettingsRow
        label="Chat widget Height (when opened)"
        isMobile={isMobile}
        element={
          <RadioGroup
           row={!isMobile}
           value={chatWidgetHeight}
           onChange={(e)=>setChatWidgetHeight(e.target.value)}
           sx={{gap:2}}
          >
            <FormControlLabel value={0.25} control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>25%</span>} />
            <FormControlLabel value={0.5} control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>50%</span>} />
            <FormControlLabel value={0.75} control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>75%</span>} />
            <FormControlLabel value={0.98} control={<Radio size="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined, '&.Mui-checked': { color: "#219297" } }} />} label={<span style={{ color: isDarkMode ? "#e2e8f0" : undefined }}>Full</span>} />
          </RadioGroup>
        }
      />
</Card>
      
{/* Popup Question Settings Card */}
<Card sx={{ 
  p: 3, 
  mb: 3, 
  borderRadius: "12px",
  backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
  border: isDarkMode ? "1px solid #2d2d3f" : "none",
  transition: "background-color 0.3s ease, border-color 0.3s ease",
}}>
  <SectionHeader
    title="Popup Question Settings"
    description="Configure the automated suggested question that appears to users"
  />

  <SettingsRow
    label="Enable Popup Question"
    isMobile={isMobile}
    element={
      <SoftBox component="label" sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={enablePopupQuestion}
          onChange={(e) => setEnablePopupQuestion(e.target.checked)}
          style={{ 
            width: 16, 
            height: 16, 
            accentColor: "#219297", 
            cursor: "pointer",
            filter: isDarkMode ? "brightness(0.9)" : "none"
          }}
        />
        <SoftTypography 
          variant="button" 
          fontWeight="medium" 
          sx={{ color: isDarkMode ? "#e2e8f0" : undefined }}
        >
          Show a suggested question to the user after a delay
        </SoftTypography>
      </SoftBox>
    }
  />

  <SettingsRow
    label="Popup Question Load Timer (seconds)"
    isMobile={isMobile}
    element={
      <SoftBox maxWidth="200px">
        <StyledInput
          type="number"
          name="popupQuestionLoadTimer"
          value={popupQuestionLoadTimer}
          onChange={(e) => setPopupQuestionLoadTimer(e.target.value)}
          placeholder="e.g. 20"
          endAdornment="sec"
        />
        <SoftTypography 
          variant="caption" 
          sx={{ 
            mt: 0.5, 
            display: "block",
            color: isDarkMode ? "#a0aec0" : "text.secondary"
          }}
        >
          Seconds before the popup question appears
        </SoftTypography>
      </SoftBox>
    }
  />

  <SettingsRow
    label="Popup Question Close Timer (seconds)"
    isMobile={isMobile}
    element={
      <SoftBox maxWidth="200px">
        <StyledInput
          type="number"
          name="popupQuestionCloseTimer"
          value={popupQuestionCloseTimer}
          onChange={(e) => setPopupQuestionCloseTimer(e.target.value)}
          placeholder="e.g. 10"
          endAdornment="sec"
        />
        <SoftTypography 
          variant="caption" 
          sx={{ 
            mt: 0.5, 
            display: "block",
            color: isDarkMode ? "#a0aec0" : "text.secondary"
          }}
        >
          Seconds before the popup auto-closes if unanswered
        </SoftTypography>
      </SoftBox>
    }
  />
</Card>
  
{/* Text & Content Card */}
<Card sx={{ 
  p: 3, 
  borderRadius: "12px",
  backgroundColor: isDarkMode ? "#060C18" : "#ffffff",
  border: isDarkMode ? "1px solid #2d2d3f" : "none",
  transition: "background-color 0.3s ease, border-color 0.3s ease",
}}>
  <SectionHeader
    title="Text & Content"
    description="Customize the text content throughout the widget"
  />

  {[
    { label: "Chat Widget Header Help Text", name: "headertext", placeholder: "Enter header help text" },
    { label: "Default Search Bar Text", name: "defaultSearchText", placeholder: "Enter default search text" },
    { label: "Message when AI cannot resolve", name: "unresolvedMessage", placeholder: "Enter unanswered query response" },
    { label: "Contact Page Label", name: "contactLabel", placeholder: "Landing page button label" },
    { label: "Contact Page URL", name: "contactURL", placeholder: "Enter landing page button URL" },
    { label: "Feedback Review Message", name: "reviewMessage", placeholder: "Enter feedback message" },
    { label: "Send Chat to Email Text", name: "emailText", placeholder: "Enter email button text" },
    { label: "Chat window Header Text", name: "chat_widget_header_text", placeholder: "Enter chat window header text" },
  ].map((field) => (
    <SettingsRow
      key={field.name}
      label={field.label}
      isMobile={isMobile}
      element={
        <StyledInput
          name={field.name}
          value={form[field.name]}
          onChange={handleChange}
          placeholder={field.placeholder}
        />
      }
    />
  ))}
</Card>

        <SoftBox sx={{ mt: 4, textAlign: { xs: "center", sm: "left" } }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={loading}
            sx={{
              borderRadius: "8px",
              px: 4,
              py: 1.5,
              fontWeight: 600,
              minWidth: { xs: "100%", sm: "auto" },
              backgroundColor: "#219297",
              color: "#fff",
              textTransform: "none",
              boxShadow: "0 4px 6px rgba(33, 146, 151, 0.2)",
              "&:hover": { 
                backgroundColor: "#1a767a",
                boxShadow: "0 6px 12px rgba(33, 146, 151, 0.3)"
              },
              "&:active": { 
                backgroundColor: "#1a767a",
                transform: "translateY(1px)"
              },
              "&:focus": { 
                backgroundColor: "#219297",
                boxShadow: "0 0 0 3px rgba(33, 146, 151, 0.2)"
              }
            }}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

export default Appearance;